import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import styled from 'styled-components';
import {
  CheckedRenderer,
  FlexColumn,
  Table,
  FlexMiddle,
  Button,
  Search,
  PromptClick
} from '@ubeya/shared-web/components';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import { ReactComponent as EmptyContactsStateIcon } from '@ubeya/shared-web/assets/empty-state-employee.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { hasRestrictedFeatures } from '@ubeya/shared/utils/accounts';
import { isEmailValid } from '@ubeya/shared/utils/form';
import ActionsRenderer from './ActionsRenderer';
import ContactModal from './ContactModal';
import InviteCRMAdminRenderer from './InviteCRMAdminRenderer';
import EmptyState from './EmptyState';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 31px 50px;
  overflow: auto;
  .loader {
    z-index: 1;
  }
`;

const Container = styled(FlexColumn)`
  flex: 1;
  min-width: 500px;
`;
const LeftSide = styled.div`
  display: flex;
  align-items: center;
`;
const RightSide = styled.div`
  display: flex;
  align-items: center;
`;

const TitleRow = styled(FlexMiddle)`
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-bottom: 25px;
  > * {
    margin-left: 10px;
  }
`;

const SendText = styled.div`
  margin-right: 10px;
`;

const Contacts = ({ inviteAdmin, initialValues = [], isClientEditable, clientId, clientOrganizationId }) => {
  const { t } = useContext(ConfigContext);
  const { addSuccess, addError } = useToaster();

  const [editContact, setEditContact] = useState();
  const [search, setSearch] = useState('');
  const { data, handleDelete, handleAdd, handleEdit } = useArrayManagement(`admins`);

  const disableEditClientContacts = !isClientEditable;

  useEffect(() => {
    initialValues?.contactName && setEditContact({});
  }, [initialValues?.contactName]);

  const handleAddContact = useCallback(() => setEditContact({}), []);

  const handleEdiContact = useCallback((item) => setEditContact(item), []);

  const hideContactPhoneNumber = hasRestrictedFeatures({
    accountId: clientId,
    organizationId: clientOrganizationId
  });

  const columns = useMemo(
    () => [
      { Header: t('firstName'), dataKey: 'firstName' },
      { Header: t('lastName'), dataKey: 'lastName' },
      { Header: t('position'), dataKey: 'role' },
      { Header: t('email'), dataKey: 'email', visible: isClientEditable },
      {
        Header: t('phone'),
        dataKey: 'phone',
        formatter: (value) => value || '',
        visible: !hideContactPhoneNumber && isClientEditable
      },
      { Header: t('active'), dataKey: 'isActivated', Cell: CheckedRenderer },
      {
        Header: t('inviteCRMAdmin'),
        Cell: ({ rowData }) => (
          <InviteCRMAdminRenderer isDisabled={rowData.id <= 0} onInvite={() => inviteAdmin(rowData.id)} />
        ),
        captureRowClick: false,
        maxWidth: 200,
        visible: isClientEditable
      }
    ],
    [hideContactPhoneNumber, inviteAdmin, isClientEditable, t]
  );

  const filteredData = useMemo(
    () =>
      data.filter(
        ({ firstName, lastName, phone }) =>
          (firstName || '').toLowerCase().includes(search.toLowerCase()) ||
          (lastName || '').toLowerCase().includes(search.toLowerCase()) ||
          `${firstName} ${lastName}`.toLowerCase().includes(search.toLowerCase()) ||
          (phone || '').toLowerCase().includes(search.toLowerCase())
      ),
    [data, search]
  );

  const nonActiveEmployees = useMemo(
    () => filteredData.filter(({ id, isActivated: isActivatedUser }) => id > 0 && !isActivatedUser),
    [filteredData]
  );

  const handleInviteUnactiveContacts = useCallback(async () => {
    await Promise.all((nonActiveEmployees || []).map(({ id }) => inviteAdmin(id)));
    addSuccess(t('mailWasSentSuccessfully'));
  }, [addSuccess, inviteAdmin, nonActiveEmployees, t]);

  const onContactAddConfirm = useCallback(
    (values) => {
      const email = values?.email;
      if (email) {
        if (!isEmailValid(email)) {
          addError(t('invalidEmail'));
          return;
        }
        const existingContact = filteredData.find((contact) => contact.email === email);
        if (existingContact) {
          addError(t('error__contact_already_exists_in_account', { email }));
          return;
        }
      }

      handleAdd(values);
    },
    [addError, t, filteredData, handleAdd]
  );

  return (
    <Wrapper>
      {!disableEditClientContacts && (
        <TitleRow>
          <LeftSide>
            <SendText>{t('CRMSendEmailToNotActiveContacts', { total: nonActiveEmployees.length || 0 })}</SendText>
            <PromptClick
              body={t('areYouSureSendEmailToUnActiveAdmins')}
              confirmText="yes"
              onClick={handleInviteUnactiveContacts}>
              <Button type="button" disabled={nonActiveEmployees.length === 0} tertiary narrow>
                {t('sendEmailNow')}
              </Button>
            </PromptClick>
          </LeftSide>
          <RightSide>
            <Search value={search} onChange={setSearch} />
            <Button type="button" secondary narrow onClick={handleAddContact}>
              + {t('addContact')}
            </Button>
          </RightSide>
        </TitleRow>
      )}

      <Container>
        <Table
          columns={columns}
          data={filteredData}
          shadow={false}
          noDataRenderer={() => <EmptyState search={search} Icon={EmptyContactsStateIcon} emptyText="noContacts" />}
          actionRenderer={
            !disableEditClientContacts &&
            (({ rowData }) => (
              <ActionsRenderer
                inviteAdmin={inviteAdmin}
                rowData={rowData}
                columnData={{ handleEdit: handleEdiContact, handleDelete }}
              />
            ))
          }
        />
      </Container>

      {editContact && (
        <ContactModal
          selectedContact={editContact}
          contatInitalValues={initialValues}
          onClose={() => setEditContact()}
          onConfirm={(values) => (editContact?.id ? handleEdit(editContact.id, values) : onContactAddConfirm(values))}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(Contacts);
